import React, { useState } from 'react';
import { Download, FileText, Settings, X, Check, FileSpreadsheet } from 'lucide-react';
import { ExportOptions, ExportProgress, EXPORT_TEMPLATES, DEFAULT_EXPORT_OPTIONS } from '../types/export.types';
import ExcelMatrixExportModal from './ExcelMatrixExportModal';
import { MatrixData, extractMatricesFromReport } from '../utils/excelMatrixExport';

interface ExportControlsProps {
  onExport: (options: ExportOptions) => Promise<void>;
  disabled?: boolean;
  className?: string;
  currentCitationState?: boolean; // Current citation display state from the report
  reportContentRef?: React.RefObject<HTMLDivElement>; // Reference to the report content for printing
  customPdfExportHandler?: () => Promise<void>; // Optional custom handler for PDF export (e.g., preview window)
  reportData?: any; // Report data containing matrices for Excel export
  clientName?: string; // Client name for Excel filename
}

export const ExportControls: React.FC<ExportControlsProps> = ({
  onExport,
  disabled = false,
  className = '',
  currentCitationState = false,
  reportContentRef,
  customPdfExportHandler,
  reportData,
  clientName
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>(DEFAULT_EXPORT_OPTIONS);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);
  const [showExcelMatrixModal, setShowExcelMatrixModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Extract matrices from report data
  const matrices: MatrixData[] = reportData ? extractMatricesFromReport(reportData) : [];

  // Debug logging to verify prop is received
  console.log('🔍🔍🔍 NEW CODE - ExportControls: customPdfExportHandler prop:', customPdfExportHandler ? 'PRESENT' : 'MISSING');

  // TEMPORARY: Alert to prove new code is loaded
  if (customPdfExportHandler) {
    console.log('✅✅✅ NEW CODE IS LOADED AND PROP IS PRESENT!');
  }

  const printReportContent = () => {
    if (!reportContentRef?.current) {
      throw new Error('Report content not found');
    }

    const reportContent = reportContentRef.current;
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (!printWindow) {
      throw new Error('Could not open print window');
    }

    // Get all CSS stylesheets from the current page
    const stylesheets = Array.from(document.styleSheets);
    let styles = '';
    
    try {
      stylesheets.forEach(sheet => {
        try {
          if (sheet.cssRules) {
            Array.from(sheet.cssRules).forEach(rule => {
              styles += rule.cssText + '\n';
            });
          }
        } catch (e) {
          // Some stylesheets might not be accessible due to CORS
          console.warn('Could not access stylesheet:', e);
        }
      });
    } catch (e) {
      console.warn('Error processing stylesheets:', e);
    }

    // Create the print document
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Workflow Report</title>
          <style>
            ${styles}
            
            /* Print-specific styles */
            @media print {
              @page {
                size: landscape;
                margin: 0.5in;
              }
              body { 
                margin: 0; 
                padding: 20px;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              button, .export-controls, .cursor-pointer {
                display: none !important;
              }
              .bg-blue-600, .bg-green-600, .bg-blue-500 {
                background-color: transparent !important;
              }
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
            
            /* Base styles for the print document */
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: none;
            }
          </style>
        </head>
        <body>
          ${reportContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleQuickExport = async () => {
    console.log('🖱️ ExportControls: handleQuickExport called');
    console.log('🔍 ExportControls: customPdfExportHandler exists?', !!customPdfExportHandler);

    // If a custom PDF export handler is provided, use it instead
    if (customPdfExportHandler) {
      console.log('✅ ExportControls: Using custom PDF export handler');
      setIsExporting(true);
      setExportProgress({
        stage: 'preparing',
        progress: 0,
        message: 'Opening PDF export preview...'
      });

      try {
        console.log('📞 ExportControls: Calling customPdfExportHandler...');
        await customPdfExportHandler();
        console.log('✅ ExportControls: customPdfExportHandler completed successfully');
        setExportProgress({
          stage: 'complete',
          progress: 100,
          message: 'Export preview opened successfully!'
        });
      } catch (error) {
        console.error('❌ ExportControls: customPdfExportHandler failed:', error);
        setExportProgress({
          stage: 'error',
          progress: 0,
          message: 'Failed to open export preview',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      } finally {
        setIsExporting(false);
        setTimeout(() => setExportProgress(null), 3000);
      }
      return;
    }

    // Default behavior: direct print dialog
    console.log('⚠️ ExportControls: No custom handler, using default print dialog');
    setIsExporting(true);
    setExportProgress({
      stage: 'preparing',
      progress: 0,
      message: 'Preparing PDF export...'
    });

    try {
      setExportProgress({
        stage: 'preparing',
        progress: 50,
        message: 'Opening print dialog for report content...'
      });

      // Print only the report content
      printReportContent();

      setExportProgress({
        stage: 'complete',
        progress: 100,
        message: 'Print dialog opened successfully!'
      });
    } catch (error) {
      setExportProgress({
        stage: 'error',
        progress: 0,
        message: 'Failed to open print dialog',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsExporting(false);
      setTimeout(() => setExportProgress(null), 3000);
    }
  };

  const handleWordExport = async () => {
    setIsExporting(true);
    setExportProgress({
      stage: 'preparing',
      progress: 0,
      message: 'Preparing Word export...'
    });

    try {
      await onExport({
        ...DEFAULT_EXPORT_OPTIONS,
        filename: `report-${Date.now()}.docx`,
        formatType: 'docx'
      });
      setExportProgress({
        stage: 'complete',
        progress: 100,
        message: 'Word document exported successfully!'
      });
    } catch (error) {
      setExportProgress({
        stage: 'error',
        progress: 0,
        message: 'Word export failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsExporting(false);
      setTimeout(() => setExportProgress(null), 3000);
    }
  };

  const handleCustomExport = async () => {
    setIsExporting(true);
    setShowOptions(false);
    setExportProgress({
      stage: 'preparing',
      progress: 0,
      message: 'Preparing export...'
    });

    try {
      setExportProgress({
        stage: 'preparing',
        progress: 50,
        message: 'Opening print dialog for report content...'
      });
      
      // Print only the report content
      printReportContent();
      
      setExportProgress({
        stage: 'complete',
        progress: 100,
        message: 'Print dialog opened successfully!'
      });
    } catch (error) {
      setExportProgress({
        stage: 'error',
        progress: 0,
        message: 'Export failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsExporting(false);
      setTimeout(() => setExportProgress(null), 3000);
    }
  };

  const handleTemplateSelect = (templateOptions: ExportOptions) => {
    setExportOptions(templateOptions);
  };

  const updateOption = (key: keyof ExportOptions, value: any) => {
    setExportOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (exportProgress) {
    return (
      <div className={`export-progress ${className}`}>
        <div className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          {exportProgress.stage === 'error' ? (
            <X className="w-5 h-5 text-red-500" />
          ) : exportProgress.stage === 'complete' ? (
            <Check className="w-5 h-5 text-green-500" />
          ) : (
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          )}
          <div className="flex-1">
            <p className={`text-sm font-medium ${
              exportProgress.stage === 'error' ? 'text-red-700' : 
              exportProgress.stage === 'complete' ? 'text-green-700' : 
              'text-blue-700'
            }`}>
              {exportProgress.message}
            </p>
            {exportProgress.error && (
              <p className="text-xs text-red-600 mt-1">{exportProgress.error}</p>
            )}
            {exportProgress.stage !== 'error' && exportProgress.stage !== 'complete' && (
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                <div 
                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${exportProgress.progress}%` }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`export-controls relative ${className}`}>
      <button
        onClick={() => setShowExportMenu((v) => !v)}
        disabled={disabled || isExporting}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        title="Export report"
        aria-haspopup="menu"
        aria-expanded={showExportMenu}
      >
        <Download className="w-3.5 h-3.5" />
        <span>Export</span>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showExportMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
          <div
            role="menu"
            className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1"
          >
            <button
              role="menuitem"
              onClick={() => { setShowExportMenu(false); handleQuickExport(); }}
              disabled={disabled || isExporting}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <Download className="w-4 h-4 text-red-600" />
              <span>Export as PDF</span>
            </button>
            <button
              role="menuitem"
              onClick={() => { setShowExportMenu(false); handleWordExport(); }}
              disabled={disabled || isExporting}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <FileText className="w-4 h-4 text-blue-600" />
              <span>Export as Word</span>
            </button>
            {matrices.length > 0 && (
              <button
                role="menuitem"
                onClick={() => { setShowExportMenu(false); setShowExcelMatrixModal(true); }}
                disabled={disabled || isExporting}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <FileSpreadsheet className="w-4 h-4 text-green-600" />
                <span>Export matrices (Excel)</span>
              </button>
            )}
          </div>
        </>
      )}

      {/* Excel Matrix Export Modal */}
      <ExcelMatrixExportModal
        isOpen={showExcelMatrixModal}
        onClose={() => setShowExcelMatrixModal(false)}
        matrices={matrices}
        clientName={clientName}
      />

      {/* Export Options Modal */}
      {showOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold" style={{ color: '#000000' }}>Export Options</h2>
              <button
                onClick={() => setShowOptions(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Templates */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-900">Quick Templates</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {EXPORT_TEMPLATES.map((template, index) => (
                  <button
                    key={index}
                    onClick={() => handleTemplateSelect(template.options)}
                    className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 text-left transition-colors"
                  >
                    <div className="font-medium text-gray-800">{template.name}</div>
                    <div className="text-sm text-gray-600 mt-1">{template.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Options */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Custom Options</h3>
              
              {/* Custom Title */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Report Title (optional)
                </label>
                <input
                  type="text"
                  value={exportOptions.customHeader || ''}
                  onChange={(e) => updateOption('customHeader', e.target.value || undefined)}
                  placeholder="Leave blank for default title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white placeholder-gray-500"
                />
              </div>

              {/* Filename */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Filename (optional)
                </label>
                <input
                  type="text"
                  value={exportOptions.filename || ''}
                  onChange={(e) => updateOption('filename', e.target.value || undefined)}
                  placeholder="Leave blank for auto-generated name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white placeholder-gray-500"
                />
              </div>

              {/* Page Orientation */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Page Orientation
                </label>
                <select
                  value={exportOptions.pageOrientation || 'portrait'}
                  onChange={(e) => updateOption('pageOrientation', e.target.value as 'portrait' | 'landscape')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                >
                  <option value="portrait" className="text-gray-900 bg-white">Portrait</option>
                  <option value="landscape" className="text-gray-900 bg-white">Landscape</option>
                </select>
              </div>

              {/* Citation Display Sync */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">
                    Citation Display Sync
                  </span>
                  <span className="text-xs text-blue-700">
                    Current display: {currentCitationState ? 'Shown' : 'Hidden'}
                  </span>
                </div>
                <button
                  onClick={() => updateOption('stripCitations', !currentCitationState)}
                  className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Match Current Display ({currentCitationState ? 'Include Citations' : 'Remove Citations'})
                </button>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportOptions.stripCitations !== false}
                    onChange={(e) => updateOption('stripCitations', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-900">
                    Clean format (remove citation symbols)
                    {exportOptions.stripCitations !== false && (
                      <span className="text-green-600 ml-1">- Recommended</span>
                    )}
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportOptions.includeMetadata !== false}
                    onChange={(e) => updateOption('includeMetadata', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-900">
                    Include metadata and document information
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportOptions.includeTimestamp !== false}
                    onChange={(e) => updateOption('includeTimestamp', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-900">
                    Include timestamp in footer
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportOptions.includeCoverPage === true}
                    onChange={(e) => updateOption('includeCoverPage', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-900">
                    Include cover page
                  </span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowOptions(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCustomExport}
                disabled={isExporting}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <FileText className="w-4 h-4 mr-2" />
                Export with Options
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportControls;